// Day.js configuration with required plugins
// This file centralizes dayjs setup to replace moment.js throughout the application

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import relativeTime from 'dayjs/plugin/relativeTime'
import localeData from 'dayjs/plugin/localeData'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/fr'
import 'dayjs/locale/pt'
import 'dayjs/locale/he'
import i18n from '../init/beforeAuth/language/i18n'

// Extend dayjs with required plugins
dayjs.extend(utc)
dayjs.extend(relativeTime) // For fromNow() functionality
dayjs.extend(localeData) // For locale data
dayjs.extend(isSameOrAfter) // For isAfter() functionality
dayjs.extend(isSameOrBefore) // For isBefore() functionality
dayjs.extend(customParseFormat) // For custom date parsing
dayjs.extend(isToday)
dayjs.extend(isYesterday)
dayjs.extend(localizedFormat)

export default dayjs

/** Keep month/time labels aligned with the active Admin UI language (Message Center, etc.). */
export const resolveDayjsLocale = (language?: string): string => {
  const base = (language || i18n.language || 'en').split('-')[0]
  const map: Record<string, string> = { en: 'en', es: 'es', fr: 'fr', pt: 'pt', he: 'he' }
  return map[base] || 'en'
}

const localizedDayjs = (date: Date | string) => dayjs(date).locale(resolveDayjsLocale())

export const formatDate = (date: Date | string): string => {
  const d = localizedDayjs(date)
  return d.isValid() ? d.format('LLL') : ''
}

const listDateWord = (key: 'today' | 'yesterday'): string => i18n.t(`common.${key}`)

/** Calendar date only (no time), aligned with Admin UI language — e.g. Licenses purchase/expiry columns. */
export const formatLocaleDate = (date: Date | string): string => {
  const d = localizedDayjs(date)
  return d.isValid() ? d.format('L') : ''
}

export const formatListDate = (date: Date | string): string => {
  const d = localizedDayjs(date)
  if (!d.isValid()) return ''
  const time = d.format('LT')
  if (d.isToday()) return `${listDateWord('today')} ${time}`
  if (d.isYesterday()) return `${listDateWord('yesterday')} ${time}`
  const datePart = d.year() === dayjs().year() ? d.format('MMM D') : d.format('MMM D, YYYY')
  return `${datePart} ${time}`
}
